import {
  ExpandMore as ExpandMoreIcon,
  PersonRemove as PersonRemoveIcon,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Fragment, useCallback, useMemo, useRef, useState } from "react";
import { fetchUsersForProject } from "../utils/api";
import { apiRequest } from "../utils/apiRequest";
import {
  getProjectById,
  getProjectsForCompany,
  hasProjectUsersInStorage,
  updateProjectUsersInStorage,
} from "../utils/sessionStorageLayer";
import { showToast } from "../utils/toastOptions";

const EXPAND_COL_WIDTH = 40;
const FIRST_CELL_PL = "4px";

const HEADER_CELL_SX = {
  py: 1.25,
  fontWeight: 600,
  fontSize: "0.75rem",
  color: "text.secondary",
  bgcolor: "grey.50",
  borderBottom: "1px solid",
  borderColor: "grey.200",
  whiteSpace: "nowrap",
};

export default function BulkUserProjectAccess({
  companyId,
  selectedUserIds = [],
  companyUsers = [],
  isAdmin,
  onAfterRemove,
}) {
  const [expandedSet, setExpandedSet] = useState(() => new Set());
  const [projectState, setProjectState] = useState({});
  const [removing, setRemoving] = useState({});
  const inflightRef = useRef(new Set());

  const projects = useMemo(
    () => (companyId != null ? getProjectsForCompany(companyId) || [] : []),
    [companyId]
  );

  const selectedSet = useMemo(
    () => new Set((selectedUserIds || []).map((id) => Number(id))),
    [selectedUserIds]
  );

  const userById = useMemo(
    () =>
      new Map(
        (companyUsers || []).map((u) => [Number(u?.id), u]).filter(([k]) => Number.isFinite(k))
      ),
    [companyUsers]
  );

  const filterUsers = useCallback(
    (projectUsers) => {
      return (projectUsers || [])
        .filter((u) => selectedSet.has(Number(u?.id)))
        .map((u) => userById.get(Number(u?.id)) || u);
    },
    [selectedSet, userById]
  );

  const loadProjectUsers = useCallback(
    async (pid) => {
      if (inflightRef.current.has(pid)) return;
      inflightRef.current.add(pid);
      setProjectState((s) => ({ ...s, [pid]: { ...s[pid], loading: true } }));
      try {
        let projectUsers;
        if (hasProjectUsersInStorage(companyId, pid)) {
          const p = getProjectById(companyId, pid);
          projectUsers = p?.users || [];
        } else {
          const fetched = await fetchUsersForProject(companyId, pid, false);
          updateProjectUsersInStorage(companyId, pid, fetched);
          projectUsers = fetched;
        }
        const matched = filterUsers(projectUsers);
        setProjectState((s) => ({
          ...s,
          [pid]: { loading: false, loaded: true, users: matched },
        }));
      } catch {
        setProjectState((s) => ({
          ...s,
          [pid]: { loading: false, loaded: true, users: [] },
        }));
      } finally {
        inflightRef.current.delete(pid);
      }
    },
    [companyId, filterUsers]
  );

  const handleAccordionChange = useCallback(
    (pid) => (_, isExpanded) => {
      setExpandedSet((prev) => {
        const next = new Set(prev);
        if (isExpanded) next.add(pid);
        else next.delete(pid);
        return next;
      });
      if (isExpanded && !projectState[pid]?.loaded) {
        loadProjectUsers(pid);
      }
    },
    [projectState, loadProjectUsers]
  );

  const handleRemove = useCallback(
    async (project, user) => {
      if (!isAdmin) return;
      const key = `${user.id}:${project.id}`;
      if (removing[key]) return;
      setRemoving((m) => ({ ...m, [key]: true }));
      try {
        await apiRequest(
          `${process.env.REACT_APP_API_BASE_URL}/companies/${companyId}/users/bulk_remove/`,
          {
            method: "POST",
            body: {
              users: [
                {
                  user_id: String(user.id),
                  project_id: String(project.id),
                },
              ],
            },
          }
        );
        showToast("success", `Removed ${user.name || "user"} from ${project.name || "project"}`);
        const fresh = await fetchUsersForProject(companyId, project.id, true);
        updateProjectUsersInStorage(companyId, project.id, fresh);
        const matched = filterUsers(fresh);
        setProjectState((s) => ({
          ...s,
          [project.id]: { loading: false, loaded: true, users: matched },
        }));
        if (onAfterRemove) await onAfterRemove();
      } catch (error) {
        showToast("error", error.message || "Failed to remove user from project");
      } finally {
        setRemoving((m) => {
          const next = { ...m };
          delete next[key];
          return next;
        });
      }
    },
    [companyId, isAdmin, onAfterRemove, removing, filterUsers]
  );

  if (!projects.length) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          No projects found for this company.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      sx={{
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: 1.5,
        bgcolor: "background.paper",
        boxShadow: "0 1px 2px rgba(16,24,40,0.05)",
        overflowX: "auto",
      }}
    >
      <Table size="small" sx={{ minWidth: 480, tableLayout: "fixed" }}>
        <TableHead>
          <TableRow sx={{ "& th": HEADER_CELL_SX }}>
            <TableCell sx={{ width: EXPAND_COL_WIDTH, px: 0 }} />
            <TableCell sx={{ pl: FIRST_CELL_PL }}>Project</TableCell>
            <TableCell sx={{ width: 170 }}>Selected Users</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {projects.map((project) => {
            const pid = project.id;
            const isExpanded = expandedSet.has(pid);
            const state = projectState[pid];
            const matched = state?.users || [];
            const toggle = () => handleAccordionChange(pid)(null, !isExpanded);

            return (
              <Fragment key={pid}>
                <TableRow
                  hover
                  onClick={toggle}
                  sx={{
                    cursor: "pointer",
                    "& > td": {
                      borderBottom: isExpanded ? "none" : "1px solid",
                      borderColor: "grey.200",
                      py: 0.5,
                    },
                    ...(isExpanded && { bgcolor: "action.hover" }),
                  }}
                >
                  <TableCell sx={{ pl: 1, pr: 0 }}>
                    <IconButton
                      size="small"
                      aria-label={isExpanded ? "Collapse" : "Expand"}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggle();
                      }}
                      sx={{
                        p: 0.25,
                        transition: "transform 150ms",
                        transform: isExpanded ? "rotate(180deg)" : "none",
                      }}
                    >
                      <ExpandMoreIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ pl: FIRST_CELL_PL }}>
                    <Typography variant="body2" noWrap>
                      {project.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {state?.loading ? (
                      <CircularProgress size={14} />
                    ) : state?.loaded ? (
                      <Chip
                        size="small"
                        label={`${matched.length} selected`}
                        color={matched.length > 0 ? "primary" : "default"}
                        variant={matched.length > 0 ? "filled" : "outlined"}
                      />
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    colSpan={3}
                    sx={{
                      p: 0,
                      borderBottom: isExpanded ? "1px solid" : "none",
                      borderColor: "grey.200",
                    }}
                  >
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box
                        sx={{
                          bgcolor: "action.hover",
                          px: `${EXPAND_COL_WIDTH + 4}px`,
                          pb: 1.5,
                          pt: 0,
                        }}
                      >
                        {state?.loading ? (
                          <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : matched.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                            None of the selected users are in this project.
                          </Typography>
                        ) : (
                          <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 1.5 }}
                          >
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ "& th": HEADER_CELL_SX }}>
                                  <TableCell>Name</TableCell>
                                  <TableCell>Email</TableCell>
                                  {isAdmin && <TableCell align="right">Quick Actions</TableCell>}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {matched.map((u) => {
                                  const key = `${u.id}:${pid}`;
                                  const isRemoving = !!removing[key];
                                  return (
                                    <TableRow key={u.id} hover>
                                      <TableCell>{u.name || "—"}</TableCell>
                                      <TableCell>{u.email_address || u.email || "—"}</TableCell>
                                      {isAdmin && (
                                        <TableCell align="right">
                                          <Tooltip title="Remove from project">
                                            <span>
                                              <IconButton
                                                size="small"
                                                color="error"
                                                disabled={isRemoving}
                                                onClick={() => handleRemove(project, u)}
                                              >
                                                {isRemoving ? (
                                                  <CircularProgress size={16} />
                                                ) : (
                                                  <PersonRemoveIcon fontSize="small" />
                                                )}
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        </TableCell>
                                      )}
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
